import { useState, type ReactNode } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { formatBRL } from '@lira/shared';
import { MoneyText } from '@/components/ui';

const EASE = [0.16, 1, 0.3, 1] as const;
const CX = 100;
const CY = 100;
const R = 78;
const SW = 24;

export interface DonutSlice {
  id: string;
  label: string;
  /** valor em centavos (inteiro). */
  value: number;
  /** cor sólida (hex da categoria). */
  color: string;
}

interface DonutChartProps {
  slices: DonutSlice[];
  /** conteúdo do furo central (ex.: <MoneyText/>). */
  centerLabel?: ReactNode;
  centerSub?: string;
}

function polar(angleDeg: number): [number, number] {
  const a = (angleDeg * Math.PI) / 180;
  return [CX + R * Math.cos(a), CY + R * Math.sin(a)];
}

/** Arco (traço, não fatia preenchida) do fração `start`→`end` (0..1), começando no topo. */
function arcPath(start: number, end: number): string {
  const [x0, y0] = polar(-90 + start * 360);
  const [x1, y1] = polar(-90 + end * 360);
  const large = end - start > 0.5 ? 1 : 0;
  return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1}`;
}

/**
 * Rosca de despesas por categoria: cada fatia é um arco em traço que varre via
 * pathLength com stagger (~40ms). Furo central mostra o total. viewBox 0 0 200 200.
 * Robusto a dados vazios (total 0 → só o trilho + centro).
 */
export function DonutChart({ slices, centerLabel, centerSub }: DonutChartProps) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<string | null>(null);
  const total = slices.reduce((sum, s) => sum + Math.max(0, s.value), 0);

  let acc = 0;
  const arcs =
    total > 0
      ? slices
          .filter((s) => s.value > 0)
          .map((s, index) => {
            const frac = s.value / total;
            const start = acc;
            acc += frac;
            return { ...s, start, frac, index, pct: Math.round(frac * 100) };
          })
      : [];

  const activeArc = active ? (arcs.find((a) => a.id === active) ?? null) : null;

  // Props comuns de interação: hover/foco troca o centro para a fatia e a
  // destaca (engrossa + esmaece as demais). Foco por teclado também ativa.
  // role="img" (não "button"): a fatia não dispara ação, só revela a leitura.
  const interaction = (id: string, label: string, pct: number, value: number) => ({
    tabIndex: 0,
    role: 'img' as const,
    'aria-label': `${label}: ${formatBRL(value)} (${pct}%)`,
    className: 'cursor-pointer outline-none [transition:stroke-width_.15s_ease,opacity_.15s_ease]',
    onMouseEnter: () => setActive(id),
    onMouseLeave: () => setActive((cur) => (cur === id ? null : cur)),
    onFocus: () => setActive(id),
    onBlur: () => setActive((cur) => (cur === id ? null : cur)),
  });
  const sliceStyle = (id: string): { strokeWidth: number; opacity: number } => ({
    strokeWidth: active === id ? SW + 5 : SW,
    opacity: active && active !== id ? 0.35 : 1,
  });

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[220px]">
      <svg
        viewBox="0 0 200 200"
        className="w-full"
        role="img"
        aria-label="Despesas por categoria"
      >
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="var(--color-border)" strokeWidth={SW} />
        {arcs.map((a) => {
          const transition = {
            duration: reduce ? 0 : 0.55,
            delay: reduce ? 0 : a.index * 0.04,
            ease: EASE,
          };
          // fatia única de 100% não pode ser um arco (início == fim) → círculo cheio.
          if (a.frac >= 0.999) {
            return (
              <m.circle
                key={a.id}
                cx={CX}
                cy={CY}
                r={R}
                fill="none"
                stroke={a.color}
                strokeLinecap="butt"
                initial={reduce ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={transition}
                style={sliceStyle(a.id)}
                {...interaction(a.id, a.label, a.pct, a.value)}
              >
                <title>{`${a.label} — ${formatBRL(a.value)} (${a.pct}%)`}</title>
              </m.circle>
            );
          }
          return (
            <m.path
              key={a.id}
              d={arcPath(a.start, a.start + a.frac)}
              fill="none"
              stroke={a.color}
              strokeLinecap="butt"
              initial={reduce ? false : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={transition}
              style={sliceStyle(a.id)}
              {...interaction(a.id, a.label, a.pct, a.value)}
            >
              <title>{`${a.label} — ${formatBRL(a.value)} (${a.pct}%)`}</title>
            </m.path>
          );
        })}
      </svg>

      {/* Centro contextual (pointer-events-none p/ não bloquear o hover das fatias):
          no hover mostra valor/nome/% da fatia; sem hover, o rótulo padrão (Total). */}
      {(centerLabel || centerSub || activeArc) && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="px-6 text-center leading-tight">
            {activeArc ? (
              <>
                <div className="text-base font-bold text-ink">
                  <MoneyText cents={activeArc.value} bold />
                </div>
                <div className="mt-0.5 flex items-center justify-center gap-1">
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: activeArc.color }}
                  />
                  <span className="max-w-[7.5rem] truncate text-[11px] font-medium text-ink">
                    {activeArc.label}
                  </span>
                </div>
                <div className="text-[11px] text-muted">{activeArc.pct}%</div>
              </>
            ) : (
              <>
                {centerLabel && <div className="text-base font-bold text-ink">{centerLabel}</div>}
                {centerSub && (
                  <div className="text-[11px] uppercase tracking-wide text-muted">{centerSub}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
